import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {
  IconLogin,
  IconMailCheck,
  IconRefresh,
  IconShieldOff,
} from '@tabler/icons-react';
import type {FC} from 'react';
import type {CheckUserStatusResponse} from 'src/api/ProactApiClient';
import {
  AuthenticationTypeEnum,
  UserValidationStatusEnum,
} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ViewField} from 'src/design/atoms/ViewField';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import type {LoginStatusTone} from './loginStatus';
import {
  describeLoginStatus,
  hasVerifiedMfaStatus,
  signInMethodLabel,
} from './loginStatus';
import {SupportDetails} from './SupportDetails';

const TONE_COLOR: Record<Exclude<LoginStatusTone, 'ok'>, string> = {
  info: 'blue',
  warning: 'yellow',
  danger: 'red',
};

export interface SignInCardProps {
  email: string;
  loginEmail: string;
  hasEmailMismatch: boolean;
  onUseLoginEmail: () => void;
  usingLoginEmail: boolean;
  status?: CheckUserStatusResponse;
  isLoading: boolean;
  errorMessage?: string;
  managedByName?: string;
  onRefresh: () => void;
  canResync: boolean;
  canDisableMfa: boolean;
  onResync: () => void;
  onDisableMfa: () => void;
  resyncing: boolean;
  disablingMfa: boolean;
  disabled: boolean;
}

export const SignInCard: FC<SignInCardProps> = ({
  email,
  loginEmail,
  hasEmailMismatch,
  onUseLoginEmail,
  usingLoginEmail,
  status: receivedStatus,
  isLoading,
  errorMessage,
  managedByName,
  onRefresh,
  canResync,
  canDisableMfa,
  onResync,
  onDisableMfa,
  resyncing,
  disablingMfa,
  disabled,
}) => {
  // Never present cached login or MFA data as current after a failed check.
  const status = errorMessage || isLoading ? undefined : receivedStatus;
  const unavailable =
    !isLoading &&
    (!status || status.status === UserValidationStatusEnum.OAuthUnknown);
  const view = status
    ? describeLoginStatus({
        status: status.status,
        authType: status.domainAuth.authType,
        providerName: status.domainAuth.providerName,
        mfaEnabled: status.mfaEnabled,
        canResync,
        managedByName,
      })
    : undefined;
  const isSso = status?.domainAuth.authType === AuthenticationTypeEnum.SSO;
  const rowDiffers = email.toLowerCase() !== loginEmail.toLowerCase();

  return (
    <SectionCard
      title="Sign-in"
      icon={<IconLogin size={IconSize.md} />}
      rightSection={
        isLoading ? (
          <Loader size="sm" />
        ) : unavailable ? (
          <Pill text="Unable to verify" color="gray" />
        ) : view ? (
          <Pill {...view.pill} />
        ) : undefined
      }>
      <Stack p="md" gap="md">
        <SimpleGrid cols={{base: 1, xs: 2}}>
          <ViewField label="Login email" value={loginEmail} />
          {status && (
            <ViewField
              label="Method"
              value={signInMethodLabel(status.domainAuth.authType)}
            />
          )}
          {status && isSso && status.domainAuth.providerName && (
            <ViewField
              label="Provider"
              value={status.domainAuth.providerName}
            />
          )}
          {status && !isSso && (
            <ViewField
              label="Multi-factor authentication"
              value={
                hasVerifiedMfaStatus(status.status)
                  ? status.mfaEnabled
                    ? 'On'
                    : 'Off'
                  : 'Unable to verify'
              }
            />
          )}
        </SimpleGrid>

        {hasEmailMismatch && (
          <InfoAlert variant="warning" title="Email mismatch">
            <Stack gap="sm">
              <Text size="sm">
                {rowDiffers
                  ? `The profile email ${email} does not match the login email ${loginEmail}.`
                  : 'A profile in another company does not use the login email.'}
              </Text>
              <Group gap="xs">
                <Button
                  variant="outline"
                  onClick={onUseLoginEmail}
                  loading={usingLoginEmail}
                  disabled={disabled}
                  leftSection={<IconMailCheck size={IconSize.md} />}>
                  Use login email
                </Button>
              </Group>
            </Stack>
          </InfoAlert>
        )}

        {!status && !isLoading && (
          <Alert color="red" variant="light" title="Sign-in status unavailable">
            {errorMessage || 'The sign-in status could not be loaded.'} Refresh
            to check again before changing the login.
          </Alert>
        )}

        {view && (
          <Box>
            <Text size="sm">{view.summary}</Text>
            {view.nextStep && view.tone === 'ok' && (
              <Text size="sm" c="dimmed" mt="xs">
                {view.nextStep}
              </Text>
            )}
            {view.nextStep && view.tone !== 'ok' && (
              <Alert color={TONE_COLOR[view.tone]} variant="light" mt="sm">
                {view.nextStep}
              </Alert>
            )}
          </Box>
        )}

        {status &&
          !isSso &&
          hasVerifiedMfaStatus(status.status) &&
          status.mfaEnabled && (
            <Text size="sm">
              Passwordless sign-in links are unavailable while multi-factor
              authentication is on.
            </Text>
          )}

        {status && !unavailable && canResync && (
          <Text size="sm">
            Resync login is for a missing login, an unusable invitation, or a
            failed password reset. It recreates the shared login and requires a
            new password. Any existing authenticator setup is removed. The
            Proact profile is kept.
          </Text>
        )}
        {status && !unavailable && canDisableMfa && (
          <Text size="sm">
            Disable MFA only to recover a lost authenticator after confirming
            the user's identity. It removes the authenticator requirement and
            signs the user out of active sessions.
          </Text>
        )}

        <Group gap="xs">
          <RefreshButton onClick={onRefresh} disabled={disabled || isLoading} />
          {status && !unavailable && canResync && (
            <Button
              variant="outline"
              color="red"
              onClick={onResync}
              loading={resyncing}
              disabled={disabled}
              leftSection={<IconRefresh size={IconSize.md} />}>
              Resync login
            </Button>
          )}
          {status && !unavailable && canDisableMfa && (
            <Button
              variant="outline"
              color="red"
              onClick={onDisableMfa}
              loading={disablingMfa}
              disabled={disabled}
              leftSection={<IconShieldOff size={IconSize.md} />}>
              Disable MFA
            </Button>
          )}
        </Group>

        {status && (
          <SupportDetails title="Sign-in help and support details">
            <ViewField
              label="Reported status"
              value={
                UserValidationStatusEnum[status.status] ??
                `Unknown (${status.status})`
              }
            />
            {isSso ? (
              <Text size="sm">
                Ask the user to enter the login email above on the Proact EHS
                login page. They will be redirected to their identity provider.
                The company IT administrator handles provider passwords and
                authenticator recovery. Contact Proact support if the redirect
                fails or the user signs in at the provider but cannot access
                Proact EHS.
              </Text>
            ) : (
              <>
                <Text size="sm">
                  Start with "Forgot Password" on the login page for a forgotten
                  password. If an invitation or reset email is missing, check
                  the address, spam folder, and Email Delivery card before
                  recreating the login.
                </Text>
                <Text size="sm">
                  After MFA recovery, the user can sign in with a password or an
                  emailed sign-in link. Help them set up their authenticator
                  again from their Security page. Disabling MFA is recorded in
                  the audit trail.
                </Text>
                <Text size="sm">
                  Resync affects the shared login used across companies. The
                  user must sign in again and normally receives a new invitation
                  to set a password. Their Proact profile is retained.
                </Text>
              </>
            )}
          </SupportDetails>
        )}
      </Stack>
    </SectionCard>
  );
};
